import { useMemo, useState } from 'react';
import { useMapEvents } from 'react-leaflet';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { PlannerWaypoint } from '../../types/missionPlanner';
import { useClusters } from '../../hooks/useClusters';
import { ClusterBadge } from '../Map/ClusterBadge';
import { WaypointMarker } from './WaypointMarker';
import { useGeofenceViolations } from '../../hooks/useGeofenceViolations';

/**
 * Above this route size, only objects inside the current viewport are rendered
 * so pan/zoom stay fluid on large routes (issue #356). Clustering (#434)
 * handles *density*; this handles *extent* — a route can be sparse but span a
 * continent, and its far end need not be in the DOM.
 */
export const CULL_THRESHOLD = 100;

/**
 * Renders the numbered, draggable waypoint markers. Waypoints closer together
 * than a marker's width collapse into one counted badge (#434) so a zoomed-out
 * route stays readable, expanding back into individual markers as the operator
 * zooms in. Large routes are additionally viewport-culled.
 *
 * Marker colour still derives from the waypoint's index in the *full* route
 * (start → end), so neither clustering nor culling can mis-label the first or
 * last leg.
 */
export const WaypointMarkerLayer: React.FC = () => {
  const waypoints = useSelector(
    (state: RootState) => state.missionPlanner.draft.waypoints
  );
  // Measured once for the whole route here rather than per marker (#451).
  const { outsideWaypointIds } = useGeofenceViolations();

  // Re-render as the viewport changes so the visible set tracks the map bounds.
  const [, bump] = useState(0);
  const map = useMapEvents({
    moveend: () => bump((v) => v + 1),
    zoomend: () => bump((v) => v + 1),
  });

  const lastIndex = waypoints.length - 1;

  const candidates = useMemo(
    () =>
      waypoints.map((wp, index) => ({
        id: wp.id,
        lat: wp.lat,
        lng: wp.lng,
        item: { waypoint: wp, index },
      })),
    [waypoints]
  );
  const clusters = useClusters<{ waypoint: PlannerWaypoint; index: number }>(
    candidates
  );

  // Pad the bounds so objects just off-screen are already mounted before they
  // scroll into view — no pop-in mid-pan.
  const bounds =
    waypoints.length > CULL_THRESHOLD ? map.getBounds().pad(0.25) : null;

  return (
    <>
      {clusters.map((cluster) => {
        if (bounds && !bounds.contains([cluster.lat, cluster.lng])) {
          return null;
        }
        if (cluster.items.length === 1) {
          const { waypoint, index } = cluster.items[0];
          return (
            <WaypointMarker
              key={waypoint.id}
              waypoint={waypoint}
              variant={
                index === 0 ? 'start' : index === lastIndex ? 'end' : 'mid'
              }
              outsideGeofence={outsideWaypointIds.has(waypoint.id)}
            />
          );
        }
        return (
          <ClusterBadge
            key={cluster.key}
            kind="waypoints"
            count={cluster.items.length}
            lat={cluster.lat}
            lng={cluster.lng}
            bounds={cluster.bounds}
          />
        );
      })}
    </>
  );
};
